import {
    type FC,
    useState,
    useEffect,
    useCallback,
    useContext,
    useRef,
    createContext,
    type RefObject,
    type ReactNode,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { type BigNumberish, type ContractReceipt } from 'ethers';
import { useCurrency } from 'dibs-buyer-layout/exports/useCurrency';
import { useWeb3Connection } from 'dibs-crypto-utils/exports/useWeb3Connection';
import { useDibsContracts } from 'dibs-crypto-utils/exports/useDibsContracts';
import { isV2Auction } from 'dibs-crypto-utils/exports/isV2Auction';
import { useMarketSettings } from 'dibs-crypto-utils/exports/useMarketSettings';
import { type MarketSettings } from 'dibs-crypto-utils/exports/useMarketSettings';
// import { type GenericERC721Token, type GenericERC1155Token } from '1stdibs-contracts/typechain';
import {
    getGenericTokenContract,
    getGeneric1155TokenContract,
} from 'dibs-crypto-utils/exports/getContract';

import * as tracking from './utils/marketTracking';
import { type useMarketContext_nftItem$key } from './__generated__/useMarketContext_nftItem.graphql';
import { type useMarketContext_viewer$key } from './__generated__/useMarketContext_viewer.graphql';

// 1stdibs-contracts not compatible with verbatimModuleSyntax
type GenericERC721Token = $TSFixMe;
type GenericERC1155Token = $TSFixMe;

type MarketStateType =
    | 'LOADING'
    | 'ERROR'
    | 'GRANT_TOKEN_APPROVAL'
    | 'LIST_TOKEN'
    | 'LIST_TOKEN_SUCCESS'
    | 'CANCEL_AUCTION'
    | 'CANCEL_AUCTION_SUCCESS';

type Props = {
    nftItem: useMarketContext_nftItem$key;
    viewer: useMarketContext_viewer$key;
    entryTrackingLabel: string;
    waitForSubscription?: boolean;
    children: ReactNode;
};

type ActionState = {
    inProgress: boolean;
    error?: string;
    finishedSuccessfully?: boolean;
};
const emptyActionState: ActionState = {
    inProgress: false,
    finishedSuccessfully: false,
    error: '',
};

type NftMarketContextType = {
    marketState: MarketStateType;
    grantTokenApproval: () => void;
    grantApprovalState: ActionState;
    cancelAuction: () => void;
    cancelAuctionState: ActionState;
    listItem: (listPrice: BigNumberish, fundsRecipient?: string) => void;
    listItemState: ActionState;
    currency: string;
    tokenName: string;
    marketSettings: MarketSettings | null;
    hasCheckedForApproval: boolean;
    hasAuctionStatusChanged: RefObject<boolean | null>;
    unfinishedTransaction: RefObject<Promise<ContractReceipt> | null>;
    isTokenApprovalGranted: boolean;
};

const NftMarketContext = createContext<NftMarketContextType>({
    marketState: 'LOADING',
    grantTokenApproval: () => {},
    grantApprovalState: { ...emptyActionState },
    cancelAuction: () => {},
    cancelAuctionState: { ...emptyActionState },
    listItem: () => {},
    listItemState: { ...emptyActionState },
    currency: 'USD',
    tokenName: '',
    marketSettings: null,
    hasCheckedForApproval: false,
    hasAuctionStatusChanged: { current: null },
    unfinishedTransaction: { current: null },
    isTokenApprovalGranted: true,
});

export const NftMarketProvider: FC<Props> = ({
    nftItem: nftItemRef,
    viewer: viewerRef,
    children,
    entryTrackingLabel,
    waitForSubscription = false,
}) => {
    const { currency } = useCurrency();
    const viewer = useFragment(
        graphql`
            fragment useMarketContext_viewer on Viewer {
                isNftAuctionV2Enabled: featureFlag(feature: "nftAuctionV2")
                ...useMarketSettings_viewer
            }
        `,
        viewerRef
    );
    const nftItem = useFragment(
        graphql`
            fragment useMarketContext_nftItem on NftItem {
                ...useMarketSettings_nftItem
                tokenId
                contract {
                    address
                    name
                }
                recentAuction: mostRecentAuction(excludeCanceled: true) {
                    auctionId
                    status
                    contractAddress {
                        address
                    }
                }
                isERC1155
            }
        `,
        nftItemRef
    );
    const tokenId = nftItem?.tokenId;
    const tokenContractAddress = nftItem.contract?.address;
    const mostRecentAuction = nftItem?.recentAuction;
    const isERC1155 = nftItem?.isERC1155;

    const marketSettings = useMarketSettings({ viewer, nftItem });

    // use v2 auction contract for creating new auctions or existing v2 auctions
    const isInAuctionV2 = isV2Auction(mostRecentAuction?.contractAddress?.address);
    const isAuctionV2Enabled = viewer?.isNftAuctionV2Enabled;

    const [initialAuctionId, setInitialAuctionId] = useState<number | null | undefined>(
        mostRecentAuction?.auctionId
    );
    const [contracts, contractsError] = useDibsContracts();
    const [tokenContract, setTokenContract] = useState<
        GenericERC721Token | GenericERC1155Token | null
    >(null);

    const { library, account: connectedAccount, alchemyProvider } = useWeb3Connection();
    const hasAuctionStatusChanged = useRef<boolean>(false);
    const unfinishedTransaction = useRef<Promise<ContractReceipt> | null>(null);

    const [hasCheckedForApproval, setHasCheckedForApproval] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [showError, setShowError] = useState(false);
    const [auctionAlreadyExists, setAuctionAlreadyExists] = useState(false);
    const [tokenName, setTokenName] = useState('');
    const [isTokenApprovalGranted, setIsTokenApprovalGranted] = useState(false);

    const [grantApprovalState, setGrantApprovalState] = useState<ActionState>({
        ...emptyActionState,
    });
    const [cancelAuctionState, setCancelAuctionState] = useState<ActionState>({
        ...emptyActionState,
    });
    const [listItemState, setListItemState] = useState<ActionState>({
        ...emptyActionState,
    });

    const trackingLabel = useRef('');

    // once contracts are available, fetch required contract info
    useEffect(() => {
        (async function fetchContractInfo() {
            if (
                contracts &&
                alchemyProvider &&
                tokenId &&
                tokenContractAddress &&
                connectedAccount
            ) {
                try {
                    const _tokenContract = isERC1155
                        ? await getGeneric1155TokenContract(alchemyProvider, tokenContractAddress)
                        : await getGenericTokenContract(alchemyProvider, tokenContractAddress);

                    if (!_tokenContract) {
                        throw new Error('could not initiate generic token contract');
                    }

                    setTokenContract(_tokenContract);
                    const currentAuctionContract =
                        contracts.auctionV2 && (isAuctionV2Enabled || isInAuctionV2)
                            ? contracts.auctionV2
                            : contracts.auction;

                    const auctionId = await currentAuctionContract.auctionIds(
                        tokenContractAddress,
                        tokenId
                    );

                    setAuctionAlreadyExists(auctionId.toNumber() !== 0);

                    if (isERC1155) {
                        setTokenName('');
                    } else {
                        setTokenName(await _tokenContract.name());
                    }

                    const isApproved =
                        (await _tokenContract.isApprovedForAll(
                            connectedAccount,
                            currentAuctionContract.address
                        )) ||
                        (!isERC1155 &&
                            (await _tokenContract.getApproved(tokenId)) ===
                                currentAuctionContract.address);
                    setIsTokenApprovalGranted(!!isApproved);
                } catch (e: $TSFixMe) {
                    setShowError(true);
                } finally {
                    setIsLoading(false);
                }
                setHasCheckedForApproval(true);
                trackingLabel.current = `${tokenContractAddress}/id/${tokenId}`;
                tracking.entry(entryTrackingLabel, trackingLabel.current);
            } else if (contractsError) {
                setShowError(true);
            }
        })();
    }, [
        contracts,
        alchemyProvider,
        contractsError,
        tokenId,
        tokenContractAddress,
        connectedAccount,
        entryTrackingLabel,
        isAuctionV2Enabled,
        isInAuctionV2,
        isERC1155,
    ]);

    // track error messages from interactions with contracts/metamask
    useEffect(() => {
        const errorMessage =
            grantApprovalState.error || cancelAuctionState.error || listItemState.error;
        if (errorMessage) {
            tracking.error(errorMessage);
        }
    }, [grantApprovalState.error, cancelAuctionState.error, listItemState.error]);

    const grantTokenApproval = useCallback(async () => {
        const signer = library?.getSigner(connectedAccount ?? '');
        const auction = isAuctionV2Enabled ? contracts?.auctionV2 : contracts?.auction;
        if (signer && tokenContract && auction && connectedAccount) {
            try {
                setGrantApprovalState({
                    error: '',
                    inProgress: true,
                });

                const txn = await tokenContract
                    .connect(signer)
                    .setApprovalForAll(auction.address, true);
                await txn.wait();

                setIsTokenApprovalGranted(true);
                setGrantApprovalState({
                    inProgress: false,
                    error: '',
                });
                setHasCheckedForApproval(true);
            } catch (e: $TSFixMe) {
                setGrantApprovalState({
                    inProgress: false,
                    error: e.message,
                });
            }
        }
    }, [connectedAccount, tokenContract, contracts, library, isAuctionV2Enabled]);

    const listItem = useCallback(
        async (listPrice: BigNumberish, fundsRecipient?: string) => {
            tracking.sellConfirm(trackingLabel.current);

            const signer = library?.getSigner(connectedAccount ?? '');
            if (
                signer &&
                tokenContract &&
                (contracts?.auctionV2 || contracts?.auction) &&
                tokenId
            ) {
                try {
                    setListItemState({ inProgress: true, error: '', finishedSuccessfully: false });
                    let txn;
                    if (isAuctionV2Enabled && contracts.auctionV2) {
                        const paymentAddress = fundsRecipient || (await signer.getAddress()); // TODO: update if a split is selected
                        txn = await contracts.auctionV2
                            .connect(signer)
                            .createAuction(
                                tokenContract.address,
                                tokenId,
                                listPrice,
                                false,
                                0,
                                0,
                                paymentAddress
                            );
                    } else {
                        txn = await contracts?.auction
                            .connect(signer)
                            .createAuction(
                                tokenContract.address,
                                tokenId,
                                listPrice,
                                false,
                                0,
                                0,
                                0
                            );
                    }

                    const awaitedTransaction = txn?.wait();
                    unfinishedTransaction.current = awaitedTransaction;
                    hasAuctionStatusChanged.current = true;

                    awaitedTransaction.then(async () => {
                        tracking.sellComplete(trackingLabel.current);

                        if (!waitForSubscription) {
                            setListItemState({
                                inProgress: false,
                                error: '',
                                finishedSuccessfully: true,
                            });
                        }
                        unfinishedTransaction.current = null;
                    });
                } catch (err: $TSFixMe) {
                    setListItemState({
                        inProgress: false,
                        error: err.message,
                        finishedSuccessfully: false,
                    });
                }
            }
        },
        [
            library,
            connectedAccount,
            tokenContract,
            contracts,
            tokenId,
            waitForSubscription,
            isAuctionV2Enabled,
        ]
    );

    const cancelAuction = useCallback(async () => {
        tracking.cancelConfirm(trackingLabel.current);

        const signer = library?.getSigner(connectedAccount ?? '');
        if (signer && tokenContract && contracts && tokenId) {
            try {
                setCancelAuctionState({
                    inProgress: true,
                    error: '',
                    finishedSuccessfully: false,
                });
                const auction =
                    isInAuctionV2 && contracts.auctionV2 ? contracts.auctionV2 : contracts.auction;
                const auctionId = await auction.auctionIds(tokenContract.address, tokenId);

                if (auctionId) {
                    const txn = await auction.connect(signer).cancelAuction(auctionId);
                    const awaitedTransaction = txn.wait();

                    unfinishedTransaction.current = awaitedTransaction;
                    hasAuctionStatusChanged.current = true;

                    awaitedTransaction.then(() => {
                        tracking.cancelComplete(trackingLabel.current);

                        setAuctionAlreadyExists(false);
                        if (!waitForSubscription) {
                            setCancelAuctionState({
                                inProgress: false,
                                error: '',
                                finishedSuccessfully: true,
                            });
                        }

                        unfinishedTransaction.current = null;
                    });
                }
            } catch (e: $TSFixMe) {
                setCancelAuctionState({
                    inProgress: false,
                    error: e.message,
                    finishedSuccessfully: false,
                });
            }
        }
    }, [
        library,
        connectedAccount,
        tokenContract,
        contracts,
        isInAuctionV2,
        tokenId,
        waitForSubscription,
    ]);

    useEffect(() => {
        if (waitForSubscription) {
            if (listItemState.inProgress && mostRecentAuction?.auctionId !== initialAuctionId) {
                setListItemState({
                    inProgress: false,
                    finishedSuccessfully: true,
                    error: '',
                });
                setInitialAuctionId(mostRecentAuction?.auctionId);
            }
            if (
                cancelAuctionState.inProgress &&
                mostRecentAuction?.auctionId !== initialAuctionId
            ) {
                setCancelAuctionState({
                    inProgress: false,
                    finishedSuccessfully: true,
                    error: '',
                });
                setInitialAuctionId(mostRecentAuction?.auctionId);
                setAuctionAlreadyExists(false);
            }
        }
    }, [
        listItemState,
        cancelAuctionState,
        waitForSubscription,
        mostRecentAuction,
        initialAuctionId,
    ]);

    const marketState: MarketStateType = (() => {
        if (isLoading) {
            return 'LOADING';
        } else if (showError) {
            return 'ERROR';
        } else if (auctionAlreadyExists || cancelAuctionState.inProgress) {
            return 'CANCEL_AUCTION';
        } else if (cancelAuctionState.finishedSuccessfully) {
            return 'CANCEL_AUCTION_SUCCESS';
        } else if (!isTokenApprovalGranted) {
            return 'GRANT_TOKEN_APPROVAL';
        } else if (listItemState.finishedSuccessfully) {
            return 'LIST_TOKEN_SUCCESS';
        } else {
            return 'LIST_TOKEN';
        }
    })();

    return (
        <NftMarketContext.Provider
            value={{
                marketState,
                grantTokenApproval,
                grantApprovalState,
                cancelAuction,
                cancelAuctionState,
                listItem,
                listItemState,
                currency,
                tokenName,
                marketSettings,
                hasCheckedForApproval,
                isTokenApprovalGranted,
                hasAuctionStatusChanged,
                unfinishedTransaction,
            }}
        >
            {children}
        </NftMarketContext.Provider>
    );
};

export const useMarketContext = (): NftMarketContextType => useContext(NftMarketContext);
